/**
 * EAA-1839: shared types for the org-scoped accounts browser
 * (`/console/hierarchy/accounts`, design frames 34/35).
 *
 * BACKEND-CONTRACT NOTES that shape every type here — read before changing them,
 * because each one is a real limitation of `GET /organizations/{id}/accounts` and
 * the two bulk endpoints rather than a UI choice:
 *
 * 1. NO SERVER SEARCH. The endpoint takes no `search` param at all, and the row
 *    shape has no name/username to search anyway (`AssignmentSerializer` →
 *    `user_sub` + `user_email` only). So there is no `search` member in
 *    `ScopedAccountsFilters`; the frame's search box renders disabled.
 * 2. NO FACET-COUNT ENDPOINT. Every count in the left sidebar is its own
 *    `page_size=1` request reading `.count` — see `useScopedAccounts`. Hence
 *    `FacetOption.count` is `number | null` ("still loading / not asked"), never
 *    `0` as a stand-in for unknown.
 * 3. `never_logged_in` DOES NOT EXIST as an issue facet and is not silently
 *    ignored either — it is a `VALIDATION_ERROR`. `FacetOption.disabledReason`
 *    exists so that row can be shown, greyed, with the true reason.
 * 4. ORDERING is limited to `granted_at` / `user_sub` / `sync_status`
 *    (`OrganizationAccountsView.ALLOWED_ORDER_FIELDS`). `ScopedAccountSortField`
 *    is exactly that set — no "last login" sort is possible.
 */

import type { ScopedAccountIssue } from '@/api/orgAdmin';

/** Exactly `OrganizationAccountsView.ALLOWED_ORDER_FIELDS`. */
export type ScopedAccountSortField = 'granted_at' | 'user_sub' | 'sync_status';

export type SortDirection = 'asc' | 'desc';

/**
 * Every server-side filter this screen drives. All single-select, matching the
 * endpoint: each param is one exact-match value, not a list.
 */
export interface ScopedAccountsFilters {
    includeChildren: boolean;
    issue: ScopedAccountIssue | null;
    levelId: string | null;
    /** Only meaningful while `includeChildren` is true — see `types.ts` note in the API client. */
    childOrgId: string | null;
    isActive: boolean | null;
}

export const EMPTY_FILTERS: ScopedAccountsFilters = {
    includeChildren: true,
    issue: null,
    levelId: null,
    childOrgId: null,
    isActive: null,
};

/** Which sidebar group an option belongs to — drives the "count everything except my own group" rule. */
export type FacetGroupKey = 'issue' | 'level' | 'childOrg' | 'status';

/**
 * EAA-1820: `FacetGroup`/`FacetOption` moved to `accounts/shared/facetTypes.ts`
 * so the realm screen's rail could finally use the SAME shape (see that file's
 * docstring) — re-exported here so every existing import in this folder
 * (`hierarchy/scopedAccounts/*`) keeps working unchanged.
 */
export type { FacetGroup, FacetOption } from '@/pages/console/accounts/shared/facetTypes';

/**
 * The row-status vocabulary this screen can actually derive, in priority order.
 *
 * Frame 34 draws "Bình thường / Chưa vào / Đã ngưng". "Chưa vào" is a login state
 * and has no data source (see the API client's `ScopedAccountRow` note), so it is
 * NOT rendered. What replaces it is real and already this app's established
 * vocabulary on `/console/hierarchy`: `is_active` plus the provisioning
 * `sync_status` the row genuinely carries.
 */
export type ScopedAccountStatus = 'revoked' | 'sync-error' | 'pending' | 'ok';
